<template>
  <ProvideApiFilebank
    v-slot="{
      loading,
      updating,
      uploading,
      uploadFiles,
      updateFile,
      deleteFiles,
      showUploaderFilter,
      onlyMyUploads,
      setOnlyMyUploads,
    }"
    :exclude-tags="excludeTags"
    @files-refreshed="handleFilesRefreshed"
    @files-deleted="handleFilesDeleted"
    @owned-tags-loaded="handleOwnedTagsLoaded"
    @uploaded="handleRecentlyUploadedFiles"
  >
    <PageHeader>
      <template #header-title>
        {{ $t('Documents') }}
      </template>
      <template #header-actions>
        <PickerFileOs
          multiple
          button-class="btn btn-md btn-success"
          class="pull-right"
          @files-selected="uploadFiles"
        >
          <template v-if="uploading">
            <ButtonSpinner />
            {{ $t('Uploading') }}
          </template>
          <template v-else>
            <i class="fa fa-upload" />
            {{ $t('Upload') }}
          </template>
        </PickerFileOs>
      </template>
    </PageHeader>
    <div class="row">
      <div class="col-sm-6">
        <div class="c-gray p-r-5">{{ $t('Show files with these tags') }}:</div>
        <WdsSelect
          v-model="selectedTags"
          :list="availableTags"
          :placeholder="$t('All tags')"
          :disabled="!availableTags.length"
          multi-select
          can-deselect
          max-labels-visible="1"
          labels-primary
        >
          <template #select-all-text>
            <WdsText :text="$t('Select all')" />
          </template>
        </WdsSelect>
      </div>
      <div v-if="showUploaderFilter" class="col-sm-6 tw-flex tw-items-end">
        <WdsToggle
          :checked="onlyMyUploads"
          :label="$t('Only show documents I uploaded')"
          data-test="filebank-only-my-uploads"
          @update:checked="setOnlyMyUploads"
        />
      </div>
    </div>

    <ModalFileEdit
      v-if="selectedFile"
      :file="selectedFile"
      :available-tags="ownedTags"
      :is-saving="updating"
      :is-open="selectedFile && isEditingFileMode"
      :show-tags="true"
      @save="saveChanges(updateFile, ...$event)"
      @canceled="cancelEditFile"
    />

    <ModalConfirmSwal
      v-if="isDeletionModalOpen"
      :header-text="$t('Are you sure?')"
      :text="deletionModalText"
      :confirm-button-text="$t('Yes, delete')"
      :processing-text="$t('Deleting')"
      @confirmed="deleteFiles(fileIdsMarkedForDeletion)"
      @cancelled="cancelDeleteFiles"
    />

    <div class="p-relative tw-mt-5">
      <BaseDataTable
        ref="table"
        :loading="loading"
        :columns="columns"
        :entries="filteredFiles"
        multi-select
        search-disabled
        @selection-updated="selectionUpdated"
      >
        <template #additional-control-bar-elements>
          <ButtonDropdown caret size="sm" class="m-l-5" :disabled="!hasSelectedFiles">
            {{ $t('Actions') }}
            <template #dropdown>
              <ul class="base-dropdown-menu" style="display: inline-block; position: static">
                <li>
                  <a href="#" @click="deleteAllSelectedFiles">
                    <i class="fa-regular fa-trash-can m-r-5" aria-hidden="true"></i>
                    {{ $t('Delete') }}
                  </a>
                </li>
              </ul>
            </template>
          </ButtonDropdown>
        </template>

        <template #name="{ row: file }">
          <FileIcon :icon="file.icon" size="sm" />
          <a :href="`/files/${file.localId}`" target="_blank" class="m-l-5">
            {{ file.name.slice(0, 40) + (file.name.length > 40 ? '...' : '') }}
          </a>
          <template v-if="file.tags">
            <span v-for="tag in file.tags" :key="tag.id" class="label label-default m-l-10">
              {{ formatTagName(tag.name) }}
            </span>
          </template>
        </template>

        <template #actions="{ row: file }">
          <BaseButton size="xs" @click="selectFileForEdit(file)">
            {{ $t('Edit') }}
          </BaseButton>
          <span v-tooltip="getDeletionWarningForFile(file)">
            <BaseButton
              size="xs"
              :disabled="!canDeleteFile(file)"
              class="m-l-5"
              @click="deleteFile(file)"
            >
              <i class="fa-regular fa-fw fa-trash-can"></i>
            </BaseButton>
          </span>
        </template>
      </BaseDataTable>
    </div>
  </ProvideApiFilebank>
</template>

<script>
import { nextTick } from 'vue'
import { WdsSelect, WdsText, WdsToggle } from '@worksome/wds-components'
import ButtonDropdown from '@/components/ButtonDropdown.vue'
import ModalConfirmSwal from '@/components/ModalConfirmSwal.vue'
import BaseDataTable from '@/components/BaseDataTable'
import ModalFileEdit from '@/components/ModalFileEdit.vue'
import BaseButton from '@/components/BaseButton.vue'
import enums from '@/utils/enum'
import { Notification } from '@/utils/notification'
import PageHeader from '@/components/PageHeader.vue'
import PickerFileOs from '@/components/PickerFileOs.vue'
import FileIcon from '@/components/FileIcon.vue'
import ButtonSpinner from '@/components/ButtonSpinner.vue'
import ProvideApiFilebank from '@/components/ProvideApiFilebank.vue'

export default {
  components: {
    ProvideApiFilebank,
    ButtonSpinner,
    FileIcon,
    WdsText,
    WdsSelect,
    WdsToggle,
    PickerFileOs,
    PageHeader,
    BaseButton,
    ModalFileEdit,
    BaseDataTable,
    ButtonDropdown,
    ModalConfirmSwal,
  },

  data() {
    return {
      columns: [
        {
          title: 'Name',
          key: 'name',
          slot: 'name',
          orderAble: false,
        },
        {
          title: 'Type',
          key: 'humanReadableType',
          orderAble: false,
        },
        {
          title: 'Size',
          key: 'humanReadableSize',
          orderAble: false,
        },
        {
          title: 'Created',
          key: 'createdAt',
          orderAble: false,
        },
        {
          title: '',
          key: 'actions',
          slot: 'actions',
          orderAble: false,
          ignoreClickForRowSelection: true,
        },
      ],
      isDeletionModalOpen: false,
      excludeTags: [
        'Expenses',
        'onboarding',
        'staffing_agency_onboarding',
        'Contract Template',
        'Assignment Confirmation Template',
      ],

      files: [], // populated by apollo
      tags: [], // populated by apollo
      ownedTags: [], // populated by apollo

      isEditingFileMode: false,
      isDeletingFileMode: false,
      isDeletingFilesMode: false,
      selectedFile: null,
      autoSelect: [],
      selectedTags: [],
      selectedFiles: [],
      selectedFilesLocalIds: [],
    }
  },

  computed: {
    availableTags() {
      const allTags = this.files.map(({ tags }) => tags.map((tag) => tag.name)).flat()

      return [...new Set(allTags)].map((name) => ({ value: name, label: name }))
    },

    selectedTagNames() {
      return this.selectedTags.map((tag) => tag.label)
    },

    filteredFiles() {
      const totalSelectedTagNames = this.selectedTagNames.length

      if (totalSelectedTagNames === 0) {
        return this.files
      }

      return this.files.filter(({ localId, tags }) => {
        const tagNamesOnFile = tags.map((t) => t.name)

        return (
          this.autoSelect.includes(localId) || // allows recently added files to show
          this.selectedTagNames.some((t) => tagNamesOnFile.includes(t))
        )
      })
    },

    hasSelectedFiles() {
      return this.selectedFiles.length > 0
    },

    deletionModalText() {
      if (this.selectedFile) {
        return this.selectedFile.attachableCount === 0
          ? this.$t('Do you want to permanently delete this file?')
          : this.$t(
              'Do you want to permanently delete this file from all places including related conversations?',
            )
      }
      return $t('Do you want to permanently delete the selected files?')
    },

    fileIdsMarkedForDeletion() {
      if (!this.isDeletingFilesMode && !this.isDeletingFileMode) {
        return []
      }

      const filesForDeletion = this.isDeletingFilesMode
        ? this.selectedFilesLocalIds
        : [this.selectedFile?.localId]

      return (
        this.files
          // Here we filter out files that should not be deleted
          .filter((file) => filesForDeletion.includes(file.localId))
          .filter(this.canDeleteFile)
          .map((file) => file.localId)
      )
    },
  },

  methods: {
    formatTagName(tagName) {
      return tagName.replaceAll('_', ' ').replace(/^\w/, (char) => char.toUpperCase())
    },

    handleInitialFilesLoaded(files) {
      this.files = files
    },

    handleFilesRefreshed(files) {
      nextTick(() => {
        this.files = files
      })
    },

    handleFilesDeleted() {
      this.selectedFile = null
      this.selectedFiles = []
      this.isDeletingFileMode = false
      this.isDeletingFilesMode = false
      this.isDeletionModalOpen = false
    },

    handleOwnedTagsLoaded(ownedTags) {
      this.ownedTags = ownedTags
    },

    selectionUpdated(data) {
      this.selectedFiles = data

      this.selectedFilesLocalIds = this.files
        .filter((file) => data.includes(file.globalId))
        .map((file) => file.localId)
    },

    deleteAllSelectedFiles() {
      this.isDeletingFilesMode = true
      this.isDeletionModalOpen = true
    },

    selectFileForEdit(file) {
      this.selectedFile = file
      this.isEditingFileMode = true
    },

    handleRecentlyUploadedFiles(files) {
      this.autoSelect = files.map((file) => file.localId)
    },

    cancelEditFile() {
      this.selectedFile = null
      this.isEditingFileMode = false
    },

    cancelDeleteFiles() {
      nextTick(() => {
        this.selectedFile = null
        this.isDeletingFileMode = false
        this.isDeletingFilesMode = false
        this.isDeletionModalOpen = false
      })
    },

    isUsedByContracts(file) {
      return !file.deletable
    },

    getDeletionWarningForFile(file) {
      if (this.isUsedByContracts(file)) {
        return `${$t('This file is in use in :no contract(s).', {
          no: file.getAttachedContractsCount,
        })} ${$t('Therefore it cannot be deleted at the moment.')}`
      }

      if (!file.viewerCanDelete) {
        return `${$t('You do not have permission to delete this file')}.`
      }

      return ''
    },
    async saveChanges(updateFileService, ...args) {
      await updateFileService(...args)
      this.selectedFile = null
      this.isEditingFileMode = false
      new Notification({
        msg: $t('Saved'),
        type: enums.notification.types.NETWORK,
        severity: enums.notification.severity.INFO,
        config: { timeout: 2000 },
      }).dispatch(this)
    },
    deleteFile(file) {
      if (this.canDeleteFile(file)) {
        this.selectedFile = file
        this.isDeletingFileMode = true
        this.isDeletionModalOpen = true
      }
    },
    canDeleteFile(file) {
      return !this.isUsedByContracts(file) && file.viewerCanDelete
    },
  },
}
</script>
